import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Legend } from 'recharts';
import { Building2, Plus, Edit, Trash2, Copy, ExternalLink, CheckCircle2, TrendingDown, Filter, Network, Users as UsersIcon, Package, Globe, LogIn, FileSpreadsheet, FileText, Radar as RadarIcon, Radio, Calendar, CircleDot, Bell, PartyPopper, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SAR from '../components/SAR';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERIOD_OPTIONS = [
  { value: 'daily', label: 'يومي' },
  { value: 'weekly', label: 'أسبوعي' },
  { value: 'monthly', label: 'شهري' },
  { value: 'quarterly', label: 'ربع سنوي' },
  { value: 'custom', label: 'تاريخ مخصص' },
];

const today = () => new Date().toISOString().split('T')[0];

const emptyForm = {
  name: '',
  slug: '',
  daily_target: 0,
  weekly_target: 0,
  monthly_target: 0,
  quarterly_target: 0,
  access_disabled: false,
};

export default function BranchesAdmin() {
  const navigate = useNavigate();
  const { setActingBranch, refreshUser } = useAuth();
  const [overview, setOverview] = useState(null);
  const [details, setDetails] = useState(null);
  const [activeTab, setActiveTab] = useState('branches');
  const [period, setPeriod] = useState('weekly');
  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState(today());
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [weeklyTargetsDialogOpen, setWeeklyTargetsDialogOpen] = useState(false);
  const [liveMode, setLiveMode] = useState(false);
  const [liveCountdown, setLiveCountdown] = useState(30);
  const [alertsHistory, setAlertsHistory] = useState([]); // [{ id, type, branch, time, message }]
  // Track which branches we've already fired each alert for (avoids spam on every refresh).
  // Shape: { achieved: Set<branchId>, lagging: Set<branchId> }
  const alertedRef = useRef({ achieved: new Set(), lagging: new Set() });
  // True only after the FIRST live-mode payload — first payload sets baseline silently.
  const liveBaselineSetRef = useRef(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // Live mode forces "daily" period (today's data, real-time)
      const effectivePeriod = liveMode ? 'daily' : period;
      const params = new URLSearchParams({ period: effectivePeriod });
      if (!liveMode && period === 'custom') {
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      }
      const [ovRes, dtRes] = await Promise.all([
        axios.get(`${API_URL}/api/super/branches-overview?${params.toString()}`),
        axios.get(`${API_URL}/api/super/network-details?${params.toString()}`),
      ]);
      setOverview(ovRes.data);
      setDetails(dtRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  }, [period, startDate, endDate, liveMode]);

  useEffect(() => {
    if (liveMode || period !== 'custom') fetchAll();
  }, [period, liveMode, fetchAll]);

  // Live mode: auto-refresh every 30s with countdown
  useEffect(() => {
    if (!liveMode) {
      setLiveCountdown(30);
      // Reset alert trackers when leaving live mode
      alertedRef.current = { achieved: new Set(), lagging: new Set() };
      liveBaselineSetRef.current = false;
      return undefined;
    }
    setLiveCountdown(30);
    const tick = setInterval(() => {
      setLiveCountdown((prev) => {
        if (prev <= 1) {
          fetchAll();
          return 30;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [liveMode, fetchAll]);

  // Live mode: smart alerts on each overview refresh
  // Rules:
  //   • achieved: branch.percentage >= 100 → green toast "🎉 فرع X حقق المستهدف!"
  //   • lagging: after Riyadh local 12:00 noon AND branch.percentage < 25 → yellow toast
  //   • Each branch fires each alert AT MOST ONCE per live session (until toggled OFF).
  //   • The very FIRST payload after entering live mode is treated as a silent baseline
  //     (so we don't spam alerts for branches that were already in either state).
  useEffect(() => {
    if (!liveMode || !overview?.branches) return;

    // Riyadh local hour (Saudi Arabia is UTC+3, no DST)
    const saHour = parseInt(
      new Intl.DateTimeFormat('en-US', { hour: 'numeric', hour12: false, timeZone: 'Asia/Riyadh' }).format(new Date()),
      10,
    );
    const isAfternoon = saHour >= 12;

    if (!liveBaselineSetRef.current) {
      // Baseline: mark currently-achieved + currently-lagging branches as already alerted
      overview.branches.forEach((b) => {
        if ((b.percentage ?? 0) >= 100) alertedRef.current.achieved.add(b.id);
        if (isAfternoon && (b.percentage ?? 0) < 25) alertedRef.current.lagging.add(b.id);
      });
      liveBaselineSetRef.current = true;
      return;
    }

    // Subsequent refreshes: detect transitions
    const newEvents = [];
    overview.branches.forEach((b) => {
      const pct = b.percentage ?? 0;
      // (1) Achieved transition
      if (pct >= 100 && !alertedRef.current.achieved.has(b.id)) {
        alertedRef.current.achieved.add(b.id);
        const message = `🎉 فرع ${b.name} حقق المستهدف!`;
        toast.success(message, {
          description: `الإنجاز ${pct}% — ${(b.achieved || 0).toLocaleString('en-US')} ر.س`,
          duration: 8000,
        });
        newEvents.push({ id: `a-${b.id}-${Date.now()}`, type: 'achieved', branch: b.name, time: new Date(), message });
      }
      // (2) Lagging transition (only after noon Riyadh time)
      if (isAfternoon && pct < 25 && !alertedRef.current.lagging.has(b.id)) {
        alertedRef.current.lagging.add(b.id);
        const message = `⚠️ فرع ${b.name} متأخر اليوم`;
        toast.warning(message, {
          description: `الإنجاز ${pct}% — تجاوز منتصف اليوم ولم يصل لـ 25%`,
          duration: 8000,
        });
        newEvents.push({ id: `l-${b.id}-${Date.now()}`, type: 'lagging', branch: b.name, time: new Date(), message });
      }
    });
    if (newEvents.length) {
      setAlertsHistory((prev) => [...newEvents, ...prev].slice(0, 20));
    }
  }, [overview, liveMode]);

  const fetchOverview = fetchAll;  // alias used by save/delete handlers

  const openAdd = () => {
    setForm(emptyForm);
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEdit = (branch) => {
    setForm({
      name: branch.name || '',
      slug: branch.slug || '',
      daily_target: branch.target ? 0 : 0,  // overview doesn't include all targets, fetched separately
      weekly_target: 0,
      monthly_target: 0,
      quarterly_target: 0,
      access_disabled: !!branch.access_disabled,
    });
    // Fetch full branch data to populate targets
    axios.get(`${API_URL}/api/branches`).then(res => {
      const full = res.data.find(b => b.id === branch.id);
      if (full) {
        setForm({
          name: full.name || '',
          slug: full.slug || '',
          daily_target: full.daily_target || 0,
          weekly_target: full.weekly_target || 0,
          monthly_target: full.monthly_target || 0,
          quarterly_target: full.quarterly_target || 0,
          access_disabled: !!full.access_disabled,
        });
      }
    });
    setEditingId(branch.id);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('اسم الفرع مطلوب');
    try {
      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim() || undefined,
        daily_target: Number(form.daily_target) || 0,
        weekly_target: Number(form.weekly_target) || 0,
        monthly_target: Number(form.monthly_target) || 0,
        quarterly_target: Number(form.quarterly_target) || 0,
        access_disabled: !!form.access_disabled,
      };
      if (editingId) {
        await axios.put(`${API_URL}/api/branches/${editingId}`, payload);
        toast.success('تم تعديل الفرع');
      } else {
        await axios.post(`${API_URL}/api/branches`, payload);
        toast.success('تم إنشاء الفرع');
      }
      setDialogOpen(false);
      fetchOverview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل الحفظ');
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null); // branch object
  const [deleteCounts, setDeleteCounts] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Open dialog: probe counts via a soft-delete attempt (no force) → backend returns 400 with counts
  const handleDelete = async (branch) => {
    setDeleteTarget(branch);
    setDeleteConfirmText('');
    setDeleteCounts(null);
    try {
      await axios.delete(`${API_URL}/api/branches/${branch.id}`);
      // No data → branch was already deleted in this case
      toast.success('تم حذف الفرع');
      setDeleteTarget(null);
      fetchOverview();
    } catch (e) {
      // Expected 400 with counts payload — parse from message
      const detail = e.response?.data?.detail || '';
      const match = /\{[^}]+\}/.exec(detail);
      if (match) {
        try {
          // detail is python-dict-like, convert single quotes to double quotes
          const parsed = JSON.parse(match[0].replace(/'/g, '"'));
          setDeleteCounts(parsed);
        } catch { setDeleteCounts(null); }
      }
      // If status is not 400 (e.g. 404, 403), surface error and close
      if (e.response?.status && e.response.status !== 400) {
        toast.error(detail || 'فشل الحذف');
        setDeleteTarget(null);
      }
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (deleteConfirmText.trim() !== deleteTarget.name.trim()) {
      toast.error('الاسم لا يطابق — اكتب اسم الفرع كما هو ظاهر');
      return;
    }
    setDeleting(true);
    try {
      const res = await axios.delete(`${API_URL}/api/branches/${deleteTarget.id}?force=true`);
      const d = res.data?.deleted || {};
      const summary = `${d.users || 0} مستخدم • ${d.employees || 0} موظف • ${d.products || 0} منتج • ${d.sales || 0} عملية بيع`;
      toast.success(`تم حذف الفرع "${deleteTarget.name}" — حُذفت ${summary}`, { duration: 7000 });
      setDeleteTarget(null);
      setDeleteConfirmText('');
      setDeleteCounts(null);
      fetchOverview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل الحذف');
    } finally {
      setDeleting(false);
    }
  };

  const copyBranchLink = (slug) => {
    const url = `${window.location.origin}/b/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('تم نسخ رابط الفرع');
  };

  const enterBranch = async (slug, name) => {
    if (!slug) return toast.error('الفرع لا يحتوي على slug');
    setActingBranch(slug);
    await refreshUser();
    toast.success(`تم الدخول لفرع ${name}`);
    navigate('/dashboard');
  };

  const [exporting, setExporting] = useState(null); // 'excel' | 'pdf' | null
  const handleExport = async (format) => {
    setExporting(format);
    try {
      const params = new URLSearchParams({ period });
      if (period === 'custom') {
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      }
      const url = `${API_URL}/api/super/network-details/export/${format}?${params.toString()}`;
      const res = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: format === 'excel'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf',
      });
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `network_report_${period}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(downloadUrl);
      toast.success(format === 'excel' ? 'تم تصدير ملف Excel' : 'تم تصدير ملف PDF');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل التصدير');
    } finally {
      setExporting(null);
    }
  };

  const branches = overview?.branches || [];
  const chartData = branches.map(b => ({
    name: b.name,
    'المستهدف': b.target,
    'المحقق': b.achieved,
  }));

  // ── Radar data: 4 dimensions, normalised 0-100 across branches ──
  // Axes: المستهدف / المحقق / نسبة الإنجاز / موظفون منتجون
  const RADAR_COLORS = ['#1E40AF', '#0F766E', '#B45309', '#9333EA', '#DB2777', '#0EA5E9', '#65A30D', '#DC2626'];
  const maxTarget = Math.max(1, ...branches.map(b => b.target || 0));
  const maxAchieved = Math.max(1, ...branches.map(b => b.achieved || 0));
  const maxPct = Math.max(100, ...branches.map(b => b.percentage || 0));
  const maxProducing = Math.max(1, ...branches.map(b => b.producing_employees || 0));
  const radarAxes = [
    { key: 'المستهدف', max: maxTarget, raw: 'target', unit: ' ر.س' },
    { key: 'المحقق', max: maxAchieved, raw: 'achieved', unit: ' ر.س' },
    { key: 'نسبة الإنجاز', max: maxPct, raw: 'percentage', unit: '%' },
    { key: 'موظفون منتجون', max: maxProducing, raw: 'producing_employees', unit: '' },
  ];
  const radarData = radarAxes.map(({ key, max, raw, unit }) => {
    const point = { axis: key };
    branches.forEach((b) => {
      const rawVal = b[raw] ?? 0;
      // Normalise to 0-100 so all axes share the same scale
      point[b.name] = max > 0 ? Math.round((rawVal / max) * 100) : 0;
      point[`${b.name}__raw`] = rawVal;
      point[`${b.name}__unit`] = unit;
    });
    return point;
  });

  const radarTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div className="bg-white border border-border rounded-lg shadow-md p-3 text-xs" dir="rtl">
        <div className="font-bold text-brand_blue mb-1.5">{label}</div>
        {payload.map((p) => {
          const branchName = p.dataKey;
          const raw = radarData.find(d => d.axis === label)?.[`${branchName}__raw`];
          const unit = radarData.find(d => d.axis === label)?.[`${branchName}__unit`] || '';
          const display = unit === ' ر.س'
            ? `${(raw ?? 0).toLocaleString('en-US')}${unit}`
            : `${raw ?? 0}${unit}`;
          return (
            <div key={branchName} className="flex items-center gap-2 py-0.5">
              <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: p.color }} />
              <span className="font-bold">{branchName}:</span>
              <span className="font-numbers">{display}</span>
              <span className="text-foreground/40">({p.value}%)</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-6" dir="rtl" data-testid="branches-admin-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-brand_blue/10 flex items-center justify-center">
            <Network className="h-6 w-6 text-brand_blue" />
          </div>
          <div>
            <h1 className="text-3xl font-black"><span className="font-brand">قِمَم</span> — المنطقة الشرقية</h1>
            <p className="text-sm text-foreground/60 mt-1">إدارة الفروع — مراقبة موحّدة • مستهدفات ومحققات • مقارنات</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={() => handleExport('excel')}
            disabled={exporting !== null || loading}
            variant="outline"
            className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="export-network-excel-btn"
          >
            <FileSpreadsheet className="h-4 w-4" />
            {exporting === 'excel' ? 'جاري التصدير...' : 'تصدير Excel'}
          </Button>
          <Button
            onClick={() => handleExport('pdf')}
            disabled={exporting !== null || loading}
            variant="outline"
            className="gap-2 border-red-300 text-red-700 hover:bg-red-50"
            data-testid="export-network-pdf-btn"
          >
            <FileText className="h-4 w-4" />
            {exporting === 'pdf' ? 'جاري التصدير...' : 'تصدير PDF'}
          </Button>
          <Button onClick={openAdd} data-testid="add-branch-btn" className="gap-2">
            <Plus className="h-4 w-4" />
            إضافة فرع جديد
          </Button>
        </div>
      </div>

      {/* Period Filter */}
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-4 w-4 text-brand_blue" />
          <h3 className="font-bold">الفترة الزمنية</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div className="space-y-2">
            <Label>النوع</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="overview-period-select" className="text-right">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {period === 'custom' && (
            <>
              <div className="space-y-2">
                <Label>من</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="text-right font-numbers" />
              </div>
              <div className="space-y-2">
                <Label>إلى</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="text-right font-numbers" />
              </div>
              <Button onClick={fetchOverview} disabled={loading} className="gap-2">
                <Filter className="h-4 w-4" />
                {loading ? 'جاري...' : 'تطبيق'}
              </Button>
            </>
          )}
        </div>
      </Card>

      {/* Overall summary */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="p-4">
            <p className="text-xs text-foreground/60">عدد الفروع</p>
            <p className="text-2xl font-black font-numbers" data-testid="overall-branches">{overview.overall.branches_count}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-foreground/60">عدد الموظفين</p>
            <p className="text-2xl font-black font-numbers">{overview.overall.employees}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-foreground/60">المستهدف الإجمالي</p>
            <p className="text-2xl font-black text-foreground font-numbers">{overview.overall.target.toLocaleString('en-US')} <SAR /></p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-foreground/60">المحقق الإجمالي</p>
            <p className="text-2xl font-black text-success font-numbers">{overview.overall.achieved.toLocaleString('en-US')} <SAR /></p>
          </Card>
          <Card className={`p-4 ${overview.overall.percentage >= 100 ? 'bg-success/10' : 'bg-amber-50'}`}>
            <p className="text-xs text-foreground/60">نسبة الإنجاز</p>
            <p className={`text-2xl font-black font-numbers ${overview.overall.percentage >= 100 ? 'text-success' : 'text-amber-700'}`} data-testid="overall-percentage">
              {overview.overall.percentage}%
            </p>
          </Card>
        </div>
      )}

      {/* Comparison chart */}
      {chartData.length > 0 && (
        <Card className="p-5">
          <h3 className="font-bold mb-4">مقارنة الفروع — المستهدف مقابل المحقق</h3>
          <div style={{ width: '100%', height: 320 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#374151' }} />
                <YAxis tick={{ fontSize: 11, fill: '#374151' }} />
                <Tooltip formatter={(v) => `${(v ?? 0).toLocaleString('en-US')} ر.س`} contentStyle={{ direction: 'rtl' }} />
                <Bar dataKey="المستهدف" fill="#94A3B8" radius={[6, 6, 0, 0]} />
                <Bar dataKey="المحقق" fill="#1E40AF" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Radar — multi-dimensional branch comparison */}
      {branches.length > 0 && (
        <Card className="p-5" data-testid="branches-radar-card">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <RadarIcon className="h-5 w-5 text-brand_blue" />
              <h3 className="font-bold">مقارنة الفروع — مخطط رادار رباعي الأبعاد</h3>
              {liveMode && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-red-50 text-red-600 text-xs font-bold border border-red-200" data-testid="live-indicator">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                  </span>
                  مباشر
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {liveMode ? (
                <span className="text-xs text-foreground/60 font-numbers" data-testid="live-countdown">
                  تحديث خلال {liveCountdown}s
                </span>
              ) : (
                <span className="text-xs text-foreground/60">
                  المحور: المستهدف · المحقق · النسبة · موظفون منتجون
                </span>
              )}
              <Button
                variant={liveMode ? 'default' : 'outline'}
                size="sm"
                onClick={() => setLiveMode((v) => !v)}
                data-testid="radar-live-toggle"
                className={`gap-2 ${liveMode ? 'bg-red-600 hover:bg-red-700 text-white' : ''}`}
              >
                {liveMode ? <Radio className="h-3.5 w-3.5" /> : <Calendar className="h-3.5 w-3.5" />}
                {liveMode ? 'إيقاف المباشر' : 'وضع مباشر'}
              </Button>
            </div>
          </div>
          <p className="text-xs text-foreground/50 mb-2">
            القيم مُطبَّعة على نسبة 0-100% لكل محور (نسبةً للحد الأقصى عبر الفروع). مرّر فوق المحور لرؤية القيم الفعلية.
          </p>
          <div style={{ width: '100%', height: 380 }}>
            <ResponsiveContainer>
              <RadarChart data={radarData} margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
                <PolarGrid stroke="#CBD5E1" />
                <PolarAngleAxis dataKey="axis" tick={{ fontSize: 12, fill: '#1E293B', fontWeight: 600 }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10, fill: '#94A3B8' }} tickFormatter={(v) => `${v}%`} />
                {branches.map((b, idx) => (
                  <Radar
                    key={b.id}
                    name={b.name}
                    dataKey={b.name}
                    stroke={RADAR_COLORS[idx % RADAR_COLORS.length]}
                    fill={RADAR_COLORS[idx % RADAR_COLORS.length]}
                    fillOpacity={0.18}
                    strokeWidth={2}
                  />
                ))}
                <Tooltip content={radarTooltip} />
                <Legend wrapperStyle={{ direction: 'rtl', paddingTop: 10 }} iconType="circle" />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          {/* Per-branch raw stats below the chart */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5 pt-5 border-t border-border">
            {branches.map((b, idx) => (
              <div key={b.id} className="rounded-lg border border-border p-3" data-testid={`radar-branch-stats-${b.slug || b.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <CircleDot className="h-3.5 w-3.5" style={{ color: RADAR_COLORS[idx % RADAR_COLORS.length] }} />
                  <span className="font-bold text-sm">{b.name}</span>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  <span className="text-foreground/50">المستهدف</span>
                  <span className="font-numbers font-bold text-left">{(b.target || 0).toLocaleString('en-US')}</span>
                  <span className="text-foreground/50">المحقق</span>
                  <span className="font-numbers font-bold text-left text-success">{(b.achieved || 0).toLocaleString('en-US')}</span>
                  <span className="text-foreground/50">النسبة</span>
                  <span className={`font-numbers font-bold text-left ${b.percentage >= 100 ? 'text-success' : 'text-amber-600'}`}>{b.percentage}%</span>
                  <span className="text-foreground/50">منتجون</span>
                  <span className="font-numbers font-bold text-left">
                    {b.producing_employees ?? 0}<span className="text-foreground/40 font-normal"> / {b.employees}</span>
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Live alerts feed (only in live mode) */}
          {liveMode && (
            <div className="mt-5 pt-5 border-t border-border" data-testid="live-alerts-feed">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4 text-brand_blue" />
                  <h4 className="font-bold text-sm">سجل التنبيهات المباشرة</h4>
                  {alertsHistory.length > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-brand_blue text-white text-xs font-bold font-numbers">
                      {alertsHistory.length}
                    </span>
                  )}
                </div>
                <p className="text-xs text-foreground/40">
                  🎉 ≥ 100% المستهدف   •   ⚠️ بعد منتصف اليوم وأقل من 25%
                </p>
              </div>
              {alertsHistory.length === 0 ? (
                <div className="text-center py-6 text-xs text-foreground/40 bg-accent/30 rounded-lg" data-testid="live-alerts-empty">
                  لا توجد تنبيهات بعد — راقب الأداء وستظهر هنا فور حدوث تغيير.
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto pl-1">
                  {alertsHistory.map((a) => (
                    <div
                      key={a.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border ${
                        a.type === 'achieved'
                          ? 'bg-success/5 border-success/30'
                          : 'bg-amber-50 border-amber-300'
                      }`}
                      data-testid={`alert-row-${a.type}`}
                    >
                      <div className={`flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center ${
                        a.type === 'achieved' ? 'bg-success/15 text-success' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {a.type === 'achieved' ? <PartyPopper className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-bold ${a.type === 'achieved' ? 'text-success' : 'text-amber-700'}`}>
                          {a.message}
                        </p>
                        <p className="text-xs text-foreground/50 font-numbers mt-0.5">
                          {a.time.toLocaleTimeString('ar-SA', {
                            hour: '2-digit', minute: '2-digit', second: '2-digit',
                            hour12: false, calendar: 'gregory', numberingSystem: 'latn',
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Tab navigation */}
      <div className="flex gap-2 border-b border-border" data-testid="overview-tabs">
        {[
          { key: 'branches', label: 'الفروع', icon: Building2 },
          { key: 'employees', label: 'الموظفون', icon: UsersIcon },
          { key: 'products', label: 'المنتجات', icon: Package },
          { key: 'countries', label: 'الدول', icon: Globe },
        ].map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-b-2 -mb-px transition ${
                activeTab === t.key
                  ? 'border-brand_blue text-brand_blue'
                  : 'border-transparent text-foreground/60 hover:text-foreground'
              }`}
              data-testid={`tab-${t.key}`}
            >
              <Icon className="h-4 w-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Branches table */}
      {activeTab === 'branches' && (
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="branches-table">
            <thead className="bg-accent/50">
              <tr>
                <th className="text-right py-3 px-4 text-sm font-bold">الفرع</th>
                <th className="text-right py-3 px-4 text-sm font-bold">الرابط</th>
                <th className="text-right py-3 px-4 text-sm font-bold">الموظفون</th>
                <th className="text-right py-3 px-4 text-sm font-bold">المستهدف</th>
                <th className="text-right py-3 px-4 text-sm font-bold">المحقق</th>
                <th className="text-right py-3 px-4 text-sm font-bold">النسبة</th>
                <th className="text-right py-3 px-4 text-sm font-bold">الحالة</th>
                <th className="text-right py-3 px-4 text-sm font-bold">إجراء</th>
              </tr>
            </thead>
            <tbody>
              {branches.length === 0 ? (
                <tr><td colSpan="8" className="text-center py-8 text-muted-foreground">لا توجد فروع</td></tr>
              ) : branches.map(b => (
                <tr key={b.id} className="border-t border-border hover:bg-accent/30">
                  <td className="py-3 px-4 font-bold flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-brand_blue" />
                    {b.name}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-accent px-2 py-0.5 rounded font-numbers">/b/{b.slug || '—'}</code>
                      {b.slug && (
                        <>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copyBranchLink(b.slug)} data-testid={`copy-link-${b.slug}`}>
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                          <a href={`/b/${b.slug}`} target="_blank" rel="noopener noreferrer">
                            <Button size="icon" variant="ghost" className="h-7 w-7"><ExternalLink className="h-3.5 w-3.5" /></Button>
                          </a>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4 font-numbers">{b.employees}</td>
                  <td className="py-3 px-4 font-numbers">{b.target.toLocaleString('en-US')} <SAR /></td>
                  <td className="py-3 px-4 text-success font-bold font-numbers">{b.achieved.toLocaleString('en-US')} <SAR /></td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-accent rounded-full overflow-hidden min-w-[60px]">
                        <div className={`h-full ${b.percentage >= 100 ? 'bg-success' : 'bg-brand_blue'}`} style={{ width: `${Math.min(b.percentage, 100)}%` }} />
                      </div>
                      <span className="font-numbers text-xs font-bold">{b.percentage}%</span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    {b.status === 'achieved' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-success/10 text-success font-bold text-xs">
                        <CheckCircle2 className="h-3 w-3" />
                        محقّق
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 font-bold text-xs">
                        <TrendingDown className="h-3 w-3" />
                        متأخّر
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-brand_blue" onClick={() => enterBranch(b.slug, b.name)} data-testid={`enter-branch-${b.slug}`} title="الدخول كمدير فرع">
                        <LogIn className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(b)} data-testid={`edit-branch-${b.slug}`}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => handleDelete(b)} data-testid={`delete-branch-${b.slug}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      )}

      {/* Employees tab */}
      {activeTab === 'employees' && (
        <Card data-testid="employees-tab">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-accent/50">
                <tr>
                  <th className="text-right py-3 px-4 text-sm font-bold">#</th>
                  <th className="text-right py-3 px-4 text-sm font-bold">الموظف</th>
                  <th className="text-right py-3 px-4 text-sm font-bold">الفرع</th>
                  <th className="text-right py-3 px-4 text-sm font-bold">المستهدف</th>
                  <th className="text-right py-3 px-4 text-sm font-bold">المحقق</th>
                  <th className="text-right py-3 px-4 text-sm font-bold">المتبقي</th>
                  <th className="text-right py-3 px-4 text-sm font-bold">المعاملات</th>
                  <th className="text-right py-3 px-4 text-sm font-bold">النسبة</th>
                  <th className="text-right py-3 px-4 text-sm font-bold">الحالة</th>
                </tr>
              </thead>
              <tbody>
                {(details?.employees || []).length === 0 ? (
                  <tr><td colSpan="9" className="text-center py-8 text-muted-foreground">لا يوجد موظفون</td></tr>
                ) : (details?.employees || []).map((e, i) => (
                  <tr key={e.id} className="border-t border-border hover:bg-accent/30">
                    <td className="py-3 px-4 font-numbers text-foreground/50">{i + 1}</td>
                    <td className="py-3 px-4 font-bold">{e.name}</td>
                    <td className="py-3 px-4 text-xs text-foreground/70">{e.branch_name}</td>
                    <td className="py-3 px-4 font-numbers">{e.target.toLocaleString('en-US')} <SAR /></td>
                    <td className="py-3 px-4 text-success font-bold font-numbers">{e.achieved.toLocaleString('en-US')} <SAR /></td>
                    <td className={`py-3 px-4 font-numbers font-bold ${e.remaining > 0 ? 'text-destructive' : 'text-success'}`}>
                      {e.remaining.toLocaleString('en-US')} <SAR />
                    </td>
                    <td className="py-3 px-4 font-numbers">{e.transactions}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-accent rounded-full overflow-hidden min-w-[60px]">
                          <div className={`h-full ${e.percentage >= 100 ? 'bg-success' : 'bg-brand_blue'}`} style={{ width: `${Math.min(e.percentage, 100)}%` }} />
                        </div>
                        <span className="font-numbers text-xs font-bold">{e.percentage}%</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {e.status === 'achieved' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-success/10 text-success font-bold text-xs">
                          <CheckCircle2 className="h-3 w-3" />محقّق
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 font-bold text-xs">
                          <TrendingDown className="h-3 w-3" />متأخّر
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Products tab */}
      {activeTab === 'products' && (
        <Card data-testid="products-tab">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-accent/50">
                <tr>
                  <th className="text-right py-3 px-4 text-sm font-bold">#</th>
                  <th className="text-right py-3 px-4 text-sm font-bold">المنتج</th>
                  <th className="text-right py-3 px-4 text-sm font-bold">إجمالي المبيعات</th>
                  <th className="text-right py-3 px-4 text-sm font-bold">الكمية</th>
                  <th className="text-right py-3 px-4 text-sm font-bold">المعاملات</th>
                  <th className="text-right py-3 px-4 text-sm font-bold">عدد الفروع</th>
                  <th className="text-right py-3 px-4 text-sm font-bold">النسبة</th>
                </tr>
              </thead>
              <tbody>
                {(details?.products || []).length === 0 ? (
                  <tr><td colSpan="7" className="text-center py-8 text-muted-foreground">لا توجد منتجات</td></tr>
                ) : (details?.products || []).map((p, i) => (
                  <tr key={p.name} className="border-t border-border hover:bg-accent/30">
                    <td className="py-3 px-4 font-numbers text-foreground/50">{i + 1}</td>
                    <td className="py-3 px-4 font-bold flex items-center gap-2">
                      <Package className="h-4 w-4 text-brand_blue" />
                      {p.name}
                    </td>
                    <td className="py-3 px-4 text-success font-bold font-numbers">{p.total.toLocaleString('en-US')} <SAR /></td>
                    <td className="py-3 px-4 font-numbers">{p.quantity}</td>
                    <td className="py-3 px-4 font-numbers">{p.count}</td>
                    <td className="py-3 px-4 font-numbers">{p.branches_count}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-accent rounded-full overflow-hidden min-w-[60px]">
                          <div className="h-full bg-brand_blue" style={{ width: `${Math.min(p.percentage, 100)}%` }} />
                        </div>
                        <span className="font-numbers text-xs font-bold">{p.percentage}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Countries tab */}
      {activeTab === 'countries' && (
        <Card data-testid="countries-tab">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-accent/50">
                <tr>
                  <th className="text-right py-3 px-4 text-sm font-bold">#</th>
                  <th className="text-right py-3 px-4 text-sm font-bold">الدولة</th>
                  <th className="text-right py-3 px-4 text-sm font-bold">إجمالي المبيعات</th>
                  <th className="text-right py-3 px-4 text-sm font-bold">المعاملات</th>
                  <th className="text-right py-3 px-4 text-sm font-bold">الكمية</th>
                  <th className="text-right py-3 px-4 text-sm font-bold">عدد الفروع</th>
                  <th className="text-right py-3 px-4 text-sm font-bold">النسبة</th>
                </tr>
              </thead>
              <tbody>
                {(details?.countries || []).length === 0 ? (
                  <tr><td colSpan="7" className="text-center py-8 text-muted-foreground">لا توجد دول</td></tr>
                ) : (details?.countries || []).map((c, i) => (
                  <tr key={c.country} className="border-t border-border hover:bg-accent/30">
                    <td className="py-3 px-4 font-numbers text-foreground/50">{i + 1}</td>
                    <td className="py-3 px-4 font-bold flex items-center gap-2">
                      <Globe className="h-4 w-4 text-brand_blue" />
                      {c.country}
                    </td>
                    <td className="py-3 px-4 text-success font-bold font-numbers">{c.total.toLocaleString('en-US')} <SAR /></td>
                    <td className="py-3 px-4 font-numbers">{c.count}</td>
                    <td className="py-3 px-4 font-numbers">{c.quantity}</td>
                    <td className="py-3 px-4 font-numbers">{c.branches_count}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-accent rounded-full overflow-hidden min-w-[60px]">
                          <div className="h-full bg-amber-500" style={{ width: `${Math.min(c.percentage, 100)}%` }} />
                        </div>
                        <span className="font-numbers text-xs font-bold">{c.percentage}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Add/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent dir="rtl" className="max-w-lg" data-testid="branch-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'تعديل الفرع' : 'إضافة فرع جديد'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>اسم الفرع</Label>
              <Input data-testid="branch-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="مثال: فرع الرياض" className="text-right" />
            </div>
            <div className="space-y-2">
              <Label>رابط الفرع (slug) <span className="text-xs text-foreground/50">— اتركه فارغاً للتوليد التلقائي</span></Label>
              <Input data-testid="branch-slug-input" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="riyadh-1" className="text-right font-numbers" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>المستهدف اليومي</Label>
                <Input type="number" value={form.daily_target} onChange={(e) => setForm({ ...form, daily_target: e.target.value })} className="font-numbers" />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label>المستهدف الأسبوعي</Label>
                  {editingId && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setWeeklyTargetsDialogOpen(true)}
                      className="h-7 text-[11px] gap-1"
                      data-testid="manage-weekly-history-btn"
                    >
                      <Calendar className="h-3 w-3" />
                      سجل الأسابيع
                    </Button>
                  )}
                </div>
                <Input type="number" value={form.weekly_target} onChange={(e) => setForm({ ...form, weekly_target: e.target.value })} className="font-numbers" />
                <p className="text-[10px] text-foreground/55">عند الحفظ، يُسجَّل هذا المبلغ كمستهدف للأسبوع الحالي تاريخياً.</p>
              </div>
              <div className="space-y-2">
                <Label>المستهدف الشهري</Label>
                <Input type="number" value={form.monthly_target} onChange={(e) => setForm({ ...form, monthly_target: e.target.value })} className="font-numbers" />
              </div>
              <div className="space-y-2">
                <Label>المستهدف الربع سنوي</Label>
                <Input type="number" value={form.quarterly_target} onChange={(e) => setForm({ ...form, quarterly_target: e.target.value })} className="font-numbers" />
              </div>
            </div>

            {/* Branch-level access lock — when ON, every user tied to this
                branch (admins, supervisors, employees) is blocked at login
                and sees "تم إيقاف الصلاحية — تواصل مع المسؤول أحمد القصبي".
                Super-admin always bypasses this lock. */}
            <div className={`rounded-lg border-2 p-3 ${form.access_disabled ? 'border-red-300 bg-red-50' : 'border-input bg-muted/20'}`} data-testid="branch-access-block">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.access_disabled}
                  onChange={(e) => setForm({ ...form, access_disabled: e.target.checked })}
                  className="mt-1 h-5 w-5 accent-red-600"
                  data-testid="branch-access-disabled-toggle"
                />
                <div className="flex-1">
                  <div className={`text-sm font-black ${form.access_disabled ? 'text-red-800' : 'text-foreground'}`}>
                    إيقاف الدخول
                  </div>
                  <div className={`text-[11px] mt-1 ${form.access_disabled ? 'text-red-700' : 'text-foreground/60'}`}>
                    عند التفعيل، لا يستطيع أي موظف أو مدير مرتبط بهذا الفرع تسجيل الدخول وتظهر له رسالة: "تم إيقاف الصلاحية — تواصل مع المسؤول أحمد القصبي".
                  </div>
                </div>
              </label>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>إلغاء</Button>
            <Button onClick={handleSave} data-testid="save-branch-btn">حفظ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-week historical targets manager */}
      <WeeklyTargetsManager
        open={weeklyTargetsDialogOpen}
        onClose={() => setWeeklyTargetsDialogOpen(false)}
        branchId={editingId}
        branchName={form.name}
      />

      {/* Delete branch confirmation dialog (cascade-delete with name typing) */}
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteConfirmText(''); setDeleteCounts(null); } }}
      >
        <DialogContent dir="rtl" className="max-w-lg" data-testid="delete-branch-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              حذف الفرع نهائياً
            </DialogTitle>
          </DialogHeader>
          {deleteTarget && (
            <div className="space-y-4">
              <div className="rounded-lg border-2 border-red-200 bg-red-50 p-4">
                <p className="text-sm text-red-900 font-bold mb-2">
                  ⚠️ تحذير — هذا الإجراء غير قابل للتراجع
                </p>
                <p className="text-xs text-red-800 leading-relaxed">
                  سيتم حذف فرع <span className="font-bold">"{deleteTarget.name}"</span> وكل بياناته نهائياً.
                </p>
                {deleteCounts && (
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs" data-testid="delete-cascade-counts">
                    <div className="bg-white border border-red-200 rounded p-2 flex items-center justify-between">
                      <span className="text-red-700">المستخدمين</span>
                      <span className="font-numbers font-bold text-red-800">{deleteCounts.users || 0}</span>
                    </div>
                    <div className="bg-white border border-red-200 rounded p-2 flex items-center justify-between">
                      <span className="text-red-700">الموظفين</span>
                      <span className="font-numbers font-bold text-red-800">{deleteCounts.employees || 0}</span>
                    </div>
                    <div className="bg-white border border-red-200 rounded p-2 flex items-center justify-between">
                      <span className="text-red-700">المنتجات</span>
                      <span className="font-numbers font-bold text-red-800">{deleteCounts.products || 0}</span>
                    </div>
                    <div className="bg-white border border-red-200 rounded p-2 flex items-center justify-between">
                      <span className="text-red-700">عمليات البيع</span>
                      <span className="font-numbers font-bold text-red-800">{deleteCounts.sales || 0}</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-sm">للتأكيد، اكتب اسم الفرع كما هو ظاهر:</Label>
                <div className="text-xs px-3 py-2 bg-accent rounded-md border border-border font-bold select-all">
                  {deleteTarget.name}
                </div>
                <Input
                  data-testid="delete-confirm-input"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="اكتب الاسم هنا للتأكيد"
                  className="text-right"
                  autoFocus
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>إلغاء</Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleting || !deleteTarget || deleteConfirmText.trim() !== (deleteTarget?.name || '').trim()}
              data-testid="confirm-delete-branch-btn"
              className="gap-2"
            >
              <Trash2 className="h-4 w-4" />
              {deleting ? 'جاري الحذف...' : 'حذف نهائي'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Sunday-based week number for a date (matches backend _saudi_week_number_for)
const sunWeekNumber = (d) => {
  const jan1 = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const offset = (jan1.getUTCDay() + 7) % 7;
  const week1Start = new Date(jan1);
  week1Start.setUTCDate(jan1.getUTCDate() - offset);
  const delta = Math.floor((d - week1Start) / 86400000);
  return { year: d.getUTCFullYear(), week: Math.floor(delta / 7) + 1 };
};

const sunWeekRange = (year, week) => {
  const jan1 = new Date(Date.UTC(year, 0, 1));
  const offset = (jan1.getUTCDay() + 7) % 7;
  const week1Start = new Date(jan1);
  week1Start.setUTCDate(jan1.getUTCDate() - offset);
  const start = new Date(week1Start);
  start.setUTCDate(week1Start.getUTCDate() + (week - 1) * 7);
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  const iso = (d) => d.toISOString().split('T')[0];
  return { start: iso(start), end: iso(end) };
};

function WeeklyTargetsManager({ open, onClose, branchId, branchName }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const _now = sunWeekNumber(new Date());
  const [year, setYear] = useState(_now.year);
  const [week, setWeek] = useState(_now.week);
  const [target, setTarget] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/branches/${branchId}/weekly-targets`);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error('تعذر تحميل السجل');
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => { if (open && branchId) load(); }, [open, branchId, load]);

  const save = async () => {
    const wt = Number(target);
    if (!Number.isFinite(wt) || wt < 0) { toast.error('قيمة المستهدف غير صحيحة'); return; }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/branches/${branchId}/weekly-targets/${year}/${week}`, { weekly_target: wt });
      toast.success(`تم حفظ مستهدف أسبوع ${week} - ${year}`);
      setTarget('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الحفظ');
    } finally { setSaving(false); }
  };

  const remove = async (yr, wk) => {
    if (!window.confirm(`حذف مستهدف الأسبوع ${wk} - ${yr}؟`)) return;
    try {
      await axios.delete(`${API_URL}/api/branches/${branchId}/weekly-targets/${yr}/${wk}`);
      toast.success('تم الحذف');
      load();
    } catch (e) { toast.error('فشل الحذف'); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent dir="rtl" className="max-w-2xl" data-testid="weekly-targets-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            سجل المستهدفات الأسبوعية — {branchName}
          </DialogTitle>
        </DialogHeader>
        {/* Add/Update form */}
        <Card className="p-3 bg-accent/30">
          <h4 className="text-sm font-bold mb-2">تعيين مستهدف لأسبوع محدد</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
            <div className="space-y-1">
              <Label className="text-[11px]">السنة</Label>
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="h-9 text-xs" data-testid="weekly-year"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 6 }, (_, i) => _now.year - 3 + i).map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">الأسبوع</Label>
              <Select value={String(week)} onValueChange={(v) => setWeek(Number(v))}>
                <SelectTrigger className="h-9 text-xs" data-testid="weekly-week"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 53 }, (_, i) => i + 1).map((w) => (
                    <SelectItem key={w} value={String(w)}>أسبوع {w}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">المستهدف (ر.س)</Label>
              <Input
                type="number"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                placeholder="مثلاً: 54000"
                className="h-9 text-xs font-numbers"
                data-testid="weekly-target-input"
              />
            </div>
            <Button onClick={save} disabled={saving || !target} className="h-9 text-xs" data-testid="weekly-target-save">
              {saving ? 'جاري الحفظ...' : 'حفظ'}
            </Button>
          </div>
          {year && week && (() => {
            const r = sunWeekRange(year, week);
            return r ? <p className="text-[10px] text-foreground/55 mt-2 font-numbers">{r.start} → {r.end}</p> : null;
          })()}
        </Card>
        {/* History list */}
        <div className="space-y-2 max-h-80 overflow-y-auto">
          <h4 className="text-sm font-bold">السجل ({items.length})</h4>
          {loading ? (
            <p className="text-xs text-foreground/50 text-center py-4">جاري التحميل...</p>
          ) : items.length === 0 ? (
            <p className="text-xs text-foreground/50 text-center py-4">لا توجد سجلات بعد</p>
          ) : (
            <div className="space-y-1.5">
              {items.map((it) => {
                const r = sunWeekRange(it.year, it.week);
                return (
                  <div key={`${it.year}-${it.week}`} className="flex items-center gap-3 p-2 bg-accent/20 rounded border border-border" data-testid={`weekly-row-${it.year}-${it.week}`}>
                    <div className="bg-primary text-white px-2 py-1 rounded text-[11px] font-bold whitespace-nowrap">
                      أسبوع {it.week} - {it.year}
                    </div>
                    <div className="text-[10px] text-foreground/55 font-numbers flex-shrink-0">{r?.start} → {r?.end}</div>
                    <div className="flex-1 text-right font-numbers font-black text-emerald-700">
                      {Number(it.weekly_target || 0).toLocaleString('en-US')} <SAR />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setYear(it.year); setWeek(it.week); setTarget(String(it.weekly_target || 0)); }}
                      className="h-7 text-[10px] gap-1"
                      data-testid={`weekly-edit-${it.year}-${it.week}`}
                    >
                      <Edit className="h-3 w-3" />تعديل
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(it.year, it.week)}
                      className="h-7 text-[10px] gap-1 text-destructive hover:text-destructive"
                      data-testid={`weekly-delete-${it.year}-${it.week}`}
                    >
                      <Trash2 className="h-3 w-3" />حذف
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>إغلاق</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
